import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError, mergeMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ReportProgressSubscription } from '@app/settings/models/reportsProgressSubcription';
import { NotificationConfig } from 'src/app/settings/models/notification';
import { Analysis } from '@shared/models/reverse-engineering/analysis.models';
import { ReportProgressWithDataModel } from '@app/settings/models/report-progress-with-data.model';
import { MetaService } from '@shared/services/meta.service';
import { AlertService } from '@app/@shared/services/alert/alert-service';
import { AlertMessages } from '@app/@shared/models/alert';


@Injectable({
  providedIn: 'root',
})
export class NotificationSettingsService {
  private readonly metaUrl = environment.metaServiceBaseUrl;
  private readonly entitlementUrl = environment.entitlementServiceUrl;

  constructor(
    private readonly http: HttpClient,
    private readonly metaService: MetaService,
    private readonly alertService: AlertService
  ) { }

  getNotificationSetup(): Observable<NotificationConfig> {
    const url = `${this.entitlementUrl}/user/me/notification-config`;
    const headers = new HttpHeaders().set('Accept', 'application/json');

    return this.http.get<NotificationConfig>(url, { headers });
  }

  updateNotificationSetup(request: NotificationConfig): Observable<any> {
    const url = `${this.entitlementUrl}/user/me/notification-config`;
    const headers = new HttpHeaders().set('Accept', 'application/json');

    return this.http.post<NotificationConfig>(url, request, { headers });
  }

  getReportsDataByProgressSubscriptions(): Observable<ReportProgressWithDataModel[]> {
    return this.getReportProgressSubscriptions().pipe(mergeMap(reportProgressSubs => {
      const reports: Observable<ReportProgressWithDataModel>[] = reportProgressSubs.map(
        progress => this.metaService.getFullReportByCode(progress.reportCode).pipe(map(report => {
          return new ReportProgressWithDataModel(report, progress);
        }))
      );
      if (reports.length > 0) {
        return forkJoin(reports);
      } else {
        return of([])
      }
    }));
  }

  getReportProgressSubscriptions(): Observable<ReportProgressSubscription[]> {
    const url = `${this.metaUrl}/progress-tracker`;
    const headers = new HttpHeaders().set('Accept', 'application/json');
    return this.http.get<ReportProgressSubscription[]>(url, { headers });
  }

  getReportProgressState(report: Analysis): Observable<ReportProgressSubscription> {
    const url = `${this.metaUrl}/progress-tracker/${report.id}`;
    const headers = new HttpHeaders().set('Accept', 'application/json');
    return this.http.get<ReportProgressSubscription>(url, { headers }).pipe(
      catchError(() => {
        return of({
          reportId: report.id,
          reportName: report.name,
          reportCode: report.code,
          notifyFirstWipDeliverable: false,
          notifyAnalysisPublished: false,
          noFirstWipInProgressTracker: true,
        })
      })
    );
  }

  followReportProgress(report: Analysis): Observable<any> {
    const reportProgress = {
      reportId: report.id,
      reportName: report.name,
      reportCode: report.code,
      notifyFirstWipDeliverable: true,
      notifyAnalysisPublished: true,
      channel: report.channel,
      subscription: report.subscriptionName,
    }
    return this.saveReportProgressSubscription(reportProgress);
  }

  saveReportProgressSubscription(reportProgress: ReportProgressSubscription): Observable<ReportProgressSubscription> {
    const url = `${this.metaUrl}/progress-tracker`;
    const headers = new HttpHeaders().set('Accept', 'application/json');
    return this.http.post<any>(url, reportProgress, { headers }).pipe(
      tap({
        next: () => this.alertService.success(AlertMessages.FOLLOW_REPORT),
        error: () => this.alertService.error(AlertMessages.FOLLOW_REPORT_FAILED)
      })
    );
  }

  deleteReportProgressSubscription(reportId: string): Observable<boolean | any> {
    const url = `${this.metaUrl}/progress-tracker/subscriptions/${reportId}`;
    const headers = new HttpHeaders().set('Accept', 'application/json');
    return this.http.delete(url, { headers }).pipe(
      tap({
        next: () => this.alertService.success(AlertMessages.UNFOLLOW_REPORT),
        error: () => this.alertService.error(AlertMessages.UNFOLLOW_REPORT_FAILED)
      })
    );
  }
}
